import { FC, memo, useCallback, useState } from 'react';
import { motion } from 'motion/react';
import { ConfidenceSlider } from './ConfidenceSlider';
import { EmotionPicker, EmotionPickerValue } from './EmotionPicker';

const ANIMATION = {
  delay: 1.5,
  stepTransitionDelay: 0.2,
  duration: 0.5,
  ease: [0.16, 1, 0.3, 1] as const,
} as const;

export interface ConfidenceProps {
  value: number;
  label?: string;
  onChange: (value: number) => void;
  onSubmit: () => void;
  onDraggingChange?: (isDragging: boolean) => void;
}

export interface EmotionProps {
  value: EmotionPickerValue | null;
  onSelect: (value: EmotionPickerValue) => void;
  onPreview: (value: EmotionPickerValue | undefined) => void;
  onSubmitStart: (value: EmotionPickerValue) => void;
}

interface BrainBreakSurveyStepsProps {
  step: 1 | 2;
  questionText: string;
  confidence: ConfidenceProps;
  emotion: EmotionProps;
}

export const BrainBreakSurveySteps: FC<BrainBreakSurveyStepsProps> = memo(
  ({ step, questionText, confidence, emotion }) => {
    const [isSliderActive, setIsSliderActive] = useState(false);

    const handleSliderActivate = useCallback((activated: boolean) => {
      if (activated) setIsSliderActive(true);
    }, []);

    const entranceDelay = step === 1 ? ANIMATION.delay : ANIMATION.stepTransitionDelay;

    return (
      <>
        <div className="tw-text-center tw-w-fit tw-h-12 tw-mx-auto tw-relative">
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: ANIMATION.duration,
              delay: entranceDelay,
              ease: ANIMATION.ease,
            }}
            className="tw-text-neutral-950 tw-text-2xl tw-m-0 tw-font-atkinson"
          >
            {questionText}
          </motion.h3>

          <div className="tw-flex tw-justify-center tw-pointer-events-none">
            {step === 1 && confidence.label && isSliderActive && (
              <motion.div
                key="confidence-label"
                initial={{ opacity: 0, y: '100%' }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: ANIMATION.duration,
                  ease: ANIMATION.ease,
                }}
              >
                <span className="tw-font-bold tw-text-blue-500 tw-text-base tw-h-5">{confidence.label}</span>
              </motion.div>
            )}

            {step === 2 && emotion.value && (
              <motion.div
                key="selected-emotion"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: ANIMATION.duration,
                  ease: ANIMATION.ease,
                }}
                className="tw-flex tw-items-center tw-gap-3"
              >
                <span className="tw-font-bold tw-text-indigo-600 tw-text-base tw-capitalize">{emotion.value.id}</span>
              </motion.div>
            )}
          </div>
        </div>

        <div className="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-w-full tw-min-h-32 tw-justify-center">
          <motion.div
            initial={step === 1 ? { opacity: 0, scale: 0 } : { opacity: 0 }}
            animate={step === 1 ? { opacity: 1, scale: 1 } : { opacity: 1 }}
            transition={
              step === 1
                ? {
                    duration: ANIMATION.duration,
                    delay: entranceDelay,
                    scale: { type: 'spring', visualDuration: ANIMATION.duration, bounce: 0.5 },
                  }
                : { duration: ANIMATION.duration, delay: entranceDelay }
            }
          >
            {step === 1 ? (
              <ConfidenceSlider
                value={confidence.value}
                onChange={confidence.onChange}
                onSubmit={confidence.onSubmit}
                onDraggingChange={confidence.onDraggingChange}
                onHoverChange={handleSliderActivate}
                onInteractChange={handleSliderActivate}
              />
            ) : (
              <EmotionPicker
                onSelect={emotion.onSelect}
                onSelectionChange={emotion.onPreview}
                onSubmitStart={emotion.onSubmitStart}
              />
            )}
          </motion.div>
        </div>
      </>
    );
  },
);

BrainBreakSurveySteps.displayName = 'BrainBreakSurveySteps';
